import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Check,
  Minus,
  Plus,
  RotateCcw,
  Volume2,
  X,
  ChevronDown,
} from "lucide-react";
import { ALPHABET_GROUPS, ALL_LETTERS } from "@/data/arabicContent";
import { PublicNav } from "@/components/public/PublicChrome";

/* =============================================================================
 * PUBLIC ALPHABET TEST — same art direction as landing / features / pricing /
 * auth (manuscript baseline, Amiri for Arabic, Newsreader display,
 * lapis-gold-harakat on paper).
 *
 * Logic is untouched: learn-first flow, question building, the --u auto-scale
 * loop (cardRef / innerRef / lastU / PASSES), scroll locking, the end screen
 * and the signup prompt. Only the skin changed.
 *
 * The scale constants and the .quiz-card / .q-inner / --u contract are what
 * fit() measures against — renaming those classes will break the scaling.
 *
 * BRAND import dropped: colors now come from the shared CSS variables below,
 * same tokens as every other public page. Decorative lucide icons (Flame,
 * Coins, Target, Clock, HelpCircle, ArrowRight) dropped with them — the end
 * screen reads as figures now.
 * ========================================================================== */

const U_MIN = 4.5;
const U_MAX = 20;
const FILL = 0.98;
const PASSES = 7;

const MODES = [
  { id: "both", label: "Mixed" },
  { id: "name", label: "Name" },
  { id: "sound", label: "Sound" },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function speak(text) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "ar-SA";
  u.rate = 0.75;
  window.speechSynthesis.speak(u);
}

function formatDuration(ms) {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function buildQuestions(letters, mode, count) {
  const pool = letters.length ? letters : ALL_LETTERS;
  const kinds =
    mode === "both" ? ["name", "sound"] : [mode === "sound" ? "sound" : "name"];

  const questions = [];
  let i = 0;
  while (questions.length < count) {
    const round = shuffle(pool);
    for (const letter of round) {
      if (questions.length >= count) break;
      const kind = kinds[i % kinds.length];
      i++;
      const key = kind === "sound" ? "sound" : "name";
      const correct = letter[key];

      const sameGroup = pool.filter(
        (l) => l.groupId === letter.groupId && l[key] !== correct,
      );
      const others = pool.filter(
        (l) => l[key] !== correct && l.groupId !== letter.groupId,
      );
      const distractors = [...shuffle(sameGroup), ...shuffle(others)]
        .map((l) => l[key])
        .filter((v, idx, arr) => arr.indexOf(v) === idx)
        .slice(0, 3);

      questions.push({
        char: letter.char,
        letter,
        kind,
        prompt:
          kind === "sound"
            ? "What sound does this letter make?"
            : "What is this letter called?",
        answer: correct,
        options: shuffle([correct, ...distractors]),
      });
    }
  }
  return questions;
}

function lettersToLearn(questions) {
  const seen = new Set();
  const out = [];
  for (const q of questions) {
    if (!seen.has(q.char)) {
      seen.add(q.char);
      out.push(q.letter);
    }
  }
  return out;
}

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Inter:wght@400;500;600&display=swap');

.hf-alpha {
--paper: #ECEDF1;
--paper-deep: #E2E4EA;
--ink: #14172A;
--ink-soft: #5D6179;
--lapis: #1F3FA8;
--gold: #B0862F;
--harakat: #B8412E;
--green: #2F7A52;
--rule: #C9CCD6;
background: var(--paper);
color: var(--ink);
min-height: 100vh;
display: flex;
flex-direction: column;
font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
-webkit-font-smoothing: antialiased;
}
.hf-alpha * { box-sizing: border-box; }
.hf-alpha h1, .hf-alpha h2 { font-family: 'Newsreader', Georgia, serif; font-weight: 500; letter-spacing: -0.015em; margin: 0; }
.hf-alpha p { margin: 0; }
.hf-alpha button { font-family: inherit; color: inherit; }
.hf-alpha :focus-visible { outline: 2px solid var(--lapis); outline-offset: 2px; }
.hf-alpha .ar, .hf-alpha .arabic { font-family: 'Amiri', 'Noto Naskh Arabic', serif; }

/* ---- quiz shell (fit() measures .quiz-card / .q-inner --u) ---- */
.hf-alpha .quiz-container {
position: fixed; top: 3.5rem; bottom: env(safe-area-inset-bottom, 0px);
left: 0; right: 0; z-index: 30; display: flex;
overflow: hidden; overscroll-behavior: contain; background: var(--paper);
}
@media (min-width: 768px) {
.hf-alpha .quiz-container { top: 5rem; bottom: 1.5rem; padding: 0 clamp(1rem, 2.5vw, 2rem); justify-content: center; }
}
.hf-alpha .quiz-card {
width: 100%; height: 100%; margin: 0 auto; display: flex; flex-direction: column;
justify-content: center; overflow: hidden; background: #fff;
container-type: inline-size; container-name: qcard;
}
@media (min-width: 768px) {
.hf-alpha .quiz-card { max-width: 1000px; border: 1px solid var(--rule); border-radius: 2px; box-shadow: 12px 12px 0 var(--paper-deep); }
}
.hf-alpha .q-inner { --u: 10px; width: 100%; display: flex; flex-direction: column; }

/* header */
.hf-alpha .q-head { padding: calc(var(--u) * 1.4) calc(var(--u) * 2.4); border-bottom: 1px solid var(--rule); }
.hf-alpha .q-head-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: calc(var(--u) * 0.8); }
.hf-alpha .q-title { font-family: 'Newsreader', Georgia, serif; font-size: calc(var(--u) * 1.9); font-weight: 500; }
.hf-alpha .q-progress-row { display: flex; justify-content: space-between; font-size: calc(var(--u) * 1.05); color: var(--ink-soft); margin-bottom: calc(var(--u) * 0.5); }
.hf-alpha .q-progress-row .ok { color: var(--green); }
.hf-alpha .q-bar { height: 1px; background: var(--rule); }
.hf-alpha .q-bar-fill { height: 1px; background: var(--ink); transition: width .3s ease-out; }
.hf-alpha .q-close { border: 1px solid var(--rule); background: transparent; border-radius: 2px; width: calc(var(--u) * 3); height: calc(var(--u) * 3); display: flex; align-items: center; justify-content: center; color: var(--ink-soft); cursor: pointer; }
.hf-alpha .q-ico-sm { width: calc(var(--u) * 1.5); height: calc(var(--u) * 1.5); }
.hf-alpha .q-ico-md { width: calc(var(--u) * 1.8); height: calc(var(--u) * 1.8); }

/* body */
.hf-alpha .q-body { padding: calc(var(--u) * 1.8) calc(var(--u) * 2.4); }
.hf-alpha .q-cols { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: calc(var(--u) * 2.2); }
@container qcard (min-width: 620px) {
.hf-alpha .q-cols { flex-direction: row; gap: calc(var(--u) * 3.4); align-items: center; }
.hf-alpha .q-col-subject, .hf-alpha .q-col-options { width: 50%; }
.hf-alpha .q-col-subject { border-right: 1px solid var(--rule); padding-right: calc(var(--u) * 3); }
}
.hf-alpha .q-col-subject { width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.hf-alpha .q-col-options { width: 100%; display: flex; flex-direction: column; }
.hf-alpha .q-char { font-family: 'Amiri', serif; font-size: calc(var(--u) * 7); line-height: 1; padding: calc(var(--u) * 0.4) 0; color: var(--ink); }
.hf-alpha .q-prompt { font-size: calc(var(--u) * 1.5); color: var(--ink-soft); text-align: center; margin: calc(var(--u) * 1.2) 0; }
.hf-alpha .q-speak { border: 1px solid var(--rule); background: transparent; border-radius: 2px; padding: calc(var(--u) * 0.8) calc(var(--u) * 1.4); color: var(--lapis); cursor: pointer; display: flex; align-items: center; justify-content: center; }
.hf-alpha .q-speak:hover { border-color: var(--lapis); }

.hf-alpha .q-option { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: calc(var(--u) * 1.2); text-align: left; cursor: pointer; border: none; border-top: 1px solid var(--rule); background: transparent; padding: calc(var(--u) * 1.15) calc(var(--u) * 0.4); font-size: calc(var(--u) * 1.55); line-height: 1.3; color: var(--ink); }
.hf-alpha .q-option:last-of-type { border-bottom: 1px solid var(--rule); }
.hf-alpha .q-option.is-right { color: var(--green); box-shadow: inset calc(var(--u) * 0.25) 0 0 var(--green); padding-left: calc(var(--u) * 1); }
.hf-alpha .q-option.is-wrong { color: var(--harakat); box-shadow: inset calc(var(--u) * 0.25) 0 0 var(--harakat); padding-left: calc(var(--u) * 1); }
.hf-alpha .q-option.is-muted { color: var(--ink-soft); opacity: .45; }
.hf-alpha .q-hint { width: 100%; margin-top: calc(var(--u) * 1.1); border: 1px solid var(--rule); background: transparent; border-radius: 2px; color: var(--ink-soft); font-size: calc(var(--u) * 1.1); padding: calc(var(--u) * 0.9); cursor: pointer; }
.hf-alpha .q-hint:hover { border-color: var(--ink-soft); }

/* learn */
.hf-alpha .q-learn-body { padding: calc(var(--u) * 1.8) calc(var(--u) * 2.4); text-align: center; display: flex; flex-direction: column; align-items: center; }
.hf-alpha .q-learn-name { font-family: 'Newsreader', Georgia, serif; font-size: calc(var(--u) * 2.2); margin-top: calc(var(--u) * 0.8); }
.hf-alpha .q-learn-sound { font-size: calc(var(--u) * 1.35); color: var(--ink-soft); margin-bottom: calc(var(--u) * 1.4); }
.hf-alpha .q-learn-foot { padding: calc(var(--u) * 1.4) calc(var(--u) * 2.4); border-top: 1px solid var(--rule); display: flex; flex-direction: column; gap: calc(var(--u) * 0.8); }
.hf-alpha .q-skip { background: transparent; border: none; color: var(--ink-soft); font-size: calc(var(--u) * 1.1); cursor: pointer; }
.hf-alpha .q-skip:hover { color: var(--ink); }

/* footer + cta */
.hf-alpha .q-foot { padding: calc(var(--u) * 1.4) calc(var(--u) * 2.4); border-top: 1px solid var(--rule); }
.hf-alpha .q-cta { width: 100%; border: 1px solid var(--ink); background: var(--ink); color: #fff; border-radius: 2px; font-weight: 600; font-size: calc(var(--u) * 1.5); padding: calc(var(--u) * 1.2); cursor: pointer; transition: background .18s ease; }
.hf-alpha .q-cta:hover { background: var(--lapis); border-color: var(--lapis); }

/* end screen */
.hf-alpha .q-end { padding: calc(var(--u) * 2.4); display: flex; flex-direction: column; align-items: center; text-align: center; }
.hf-alpha .q-end-title { font-family: 'Newsreader', Georgia, serif; font-size: calc(var(--u) * 2.6); margin-bottom: calc(var(--u) * 1.8); }
.hf-alpha .q-stats { display: grid; grid-template-columns: repeat(4, 1fr); width: 100%; border-top: 1px solid var(--rule); border-bottom: 1px solid var(--rule); margin-bottom: calc(var(--u) * 1.6); }
.hf-alpha .q-stat { padding: calc(var(--u) * 1.2) calc(var(--u) * 0.6); border-left: 1px solid var(--rule); }
.hf-alpha .q-stat:first-child { border-left: none; }
.hf-alpha .q-stat-value { font-family: 'Newsreader', Georgia, serif; font-size: calc(var(--u) * 2.2); line-height: 1.1; }
.hf-alpha .q-stat-label { font-size: calc(var(--u) * 0.95); color: var(--ink-soft); margin-top: calc(var(--u) * 0.2); }
.hf-alpha .q-stat.accent .q-stat-value { color: var(--lapis); }
.hf-alpha .q-details { display: flex; align-items: center; justify-content: center; gap: calc(var(--u) * 0.5); background: transparent; border: none; color: var(--ink-soft); font-size: calc(var(--u) * 1.1); cursor: pointer; margin-bottom: calc(var(--u) * 1.1); }
.hf-alpha .q-chevron { width: calc(var(--u) * 1.2); height: calc(var(--u) * 1.2); transition: transform .2s; }
.hf-alpha .q-chevron.open { transform: rotate(180deg); }
.hf-alpha .q-badge { color: var(--harakat); border: 1px solid var(--harakat); border-radius: 2px; font-size: calc(var(--u) * 0.9); padding: 0 calc(var(--u) * 0.5); }
.hf-alpha .q-wrong-chars { font-family: 'Amiri', serif; font-size: calc(var(--u) * 2.2); display: flex; gap: calc(var(--u) * 1); flex-wrap: wrap; justify-content: center; margin-bottom: calc(var(--u) * 1.6); color: var(--harakat); }
.hf-alpha .q-actions { display: flex; flex-direction: column; gap: calc(var(--u) * 0.8); width: 100%; }
.hf-alpha .q-redo { width: 100%; display: flex; align-items: center; justify-content: center; gap: calc(var(--u) * 0.6); font-size: calc(var(--u) * 1.3); font-weight: 600; padding: calc(var(--u) * 1.1); border-radius: 2px; background: transparent; border: 1px solid var(--rule); color: var(--ink-soft); cursor: pointer; }
.hf-alpha .q-redo:hover { border-color: var(--ink); color: var(--ink); }

/* signup prompt */
.hf-alpha .signup-note { position: fixed; bottom: 1rem; left: 1rem; right: 1rem; z-index: 50; background: var(--ink); color: #fff; border-radius: 2px; padding: 18px 20px; cursor: pointer; display: flex; gap: 16px; align-items: flex-start; }
@media (min-width: 768px) { .hf-alpha .signup-note { left: auto; right: 1.5rem; bottom: 1.5rem; width: 370px; } }
.hf-alpha .signup-note .ar-mark { font-family: 'Amiri', serif; font-size: 30px; color: var(--gold); line-height: 1; }
.hf-alpha .signup-note h3 { font-family: 'Newsreader', Georgia, serif; font-size: 18px; margin-bottom: 4px; }
.hf-alpha .signup-note p { font-size: 13px; color: #A7ACC4; margin-bottom: 10px; }
.hf-alpha .signup-note .go { font-size: 13px; font-weight: 600; color: var(--gold); border-bottom: 1px solid var(--gold); }
.hf-alpha .signup-close { position: absolute; top: 10px; right: 10px; background: none; border: none; color: #A7ACC4; cursor: pointer; }

/* ---- settings screen ---- */
.hf-alpha .set-page { flex: 1; width: 100%; max-width: 620px; margin: 0 auto; padding: 44px 24px calc(env(safe-area-inset-bottom, 0px) + 96px); }
.hf-alpha .set-page h1 { font-size: 34px; letter-spacing: -0.02em; }
.hf-alpha .set-page .set-sub { color: var(--ink-soft); font-size: 14.5px; margin-top: 6px; }
.hf-alpha .set-rule { height: 1px; background: linear-gradient(90deg, var(--ink) 0 22%, var(--rule) 22% 100%); margin: 24px 0 8px; }
.hf-alpha .set-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 18px 0; border-bottom: 1px solid var(--rule); }
.hf-alpha .set-label { font-size: 14.5px; font-weight: 600; }
.hf-alpha .seg { display: flex; }
.hf-alpha .seg button { border: 1px solid var(--rule); border-left: none; background: transparent; padding: 7px 14px; font-size: 13px; color: var(--ink-soft); cursor: pointer; }
.hf-alpha .seg button:first-child { border-left: 1px solid var(--rule); border-radius: 2px 0 0 2px; }
.hf-alpha .seg button:last-child { border-radius: 0 2px 2px 0; }
.hf-alpha .seg button.on { background: var(--ink); border-color: var(--ink); color: #fff; }
.hf-alpha .step { display: flex; align-items: center; gap: 4px; }
.hf-alpha .step button { width: 32px; height: 32px; border: 1px solid var(--rule); background: transparent; border-radius: 2px; display: flex; align-items: center; justify-content: center; color: var(--ink-soft); cursor: pointer; }
.hf-alpha .step button:hover { border-color: var(--ink); color: var(--ink); }
.hf-alpha .step-val { font-family: 'Newsreader', Georgia, serif; font-size: 21px; width: 38px; text-align: center; font-variant-numeric: tabular-nums; }
.hf-alpha .grp-head { display: flex; align-items: baseline; justify-content: space-between; margin: 30px 0 0; }
.hf-alpha .grp-head span { font-size: 14.5px; font-weight: 600; }
.hf-alpha .grp-head button { background: none; border: none; font-size: 13px; color: var(--lapis); cursor: pointer; border-bottom: 1px solid var(--lapis); padding: 0 0 1px; }
.hf-alpha .grp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 28px; }
.hf-alpha .grp { text-align: left; background: transparent; border: none; border-top: 1px solid var(--rule); padding: 14px 2px; cursor: pointer; display: block; width: 100%; }
.hf-alpha .grp-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.hf-alpha .grp-name { font-size: 13.5px; font-weight: 600; color: var(--ink-soft); }
.hf-alpha .grp.on .grp-name { color: var(--ink); }
.hf-alpha .grp-letters { font-family: 'Amiri', serif; font-size: 21px; line-height: 1.3; color: var(--ink-soft); opacity: .5; margin-top: 4px; }
.hf-alpha .grp.on .grp-letters { color: var(--lapis); opacity: 1; }
.hf-alpha .action-bar { position: fixed; bottom: 0; left: 0; right: 0; padding: 14px 24px calc(env(safe-area-inset-bottom, 0px) + 14px); background: var(--paper); border-top: 1px solid var(--rule); z-index: 10; }
.hf-alpha .action-bar .inner { max-width: 620px; margin: 0 auto; }
.hf-alpha .start-btn { width: 100%; background: var(--ink); color: #fff; border: 1px solid var(--ink); border-radius: 2px; padding: 15px; font-size: 15px; font-weight: 600; cursor: pointer; transition: background .18s ease; }
.hf-alpha .start-btn:hover:not(:disabled) { background: var(--lapis); border-color: var(--lapis); }
.hf-alpha .start-btn:disabled { opacity: .4; cursor: default; }

@media (prefers-reduced-motion: reduce) { .hf-alpha * { transition: none !important; animation: none !important; } }
`;

export default function PublicAlphabet() {
  const navigate = useNavigate();

  const [mode, setMode] = useState("both");
  const [count, setCount] = useState(15);
  const [selected, setSelected] = useState(ALPHABET_GROUPS.map((g) => g.id));

  const [learnLetters, setLearnLetters] = useState(null);
  const [learnIndex, setLearnIndex] = useState(0);
  const [pendingSession, setPendingSession] = useState(null);

  const [session, setSession] = useState(null);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [skipped, setSkipped] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [done, setDone] = useState(false);

  const [bestStreak, setBestStreak] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [showToast, setShowToast] = useState(true);

  const streakRef = useRef(0);
  const startTimeRef = useRef(null);

  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const lastU = useRef(10);

  useEffect(() => {
    document.title = "Arabic Alphabet Test — Harf";
  }, []);

  /* ------------------------------------------------------- Auto-scale */
  const fit = useCallback(() => {
    const card = cardRef.current;
    const inner = innerRef.current;
    if (!card || !inner) return;

    const avail = card.clientHeight;
    const width = card.clientWidth;
    if (!avail || !width) return;

    const uMax = Math.min(U_MAX, width / 24);
    const target = avail * FILL;

    let u = Math.min(Math.max(lastU.current, U_MIN), uMax);

    for (let pass = 0; pass < PASSES; pass++) {
      inner.style.setProperty("--u", `${u}px`);
      const measured = inner.scrollHeight;
      if (!measured) break;

      const ratio = target / measured;
      if (ratio > 0.99 && ratio < 1.01) break;

      const nextU = Math.min(Math.max(u * ratio, U_MIN), uMax);
      if (Math.abs(nextU - u) < 0.05) {
        u = nextU;
        break;
      }
      u = nextU;
    }

    inner.style.setProperty("--u", `${u}px`);
    lastU.current = u;
  }, []);

  useLayoutEffect(() => {
    if (session || learnLetters) fit();
  }, [
    fit,
    index,
    picked,
    skipped,
    session,
    done,
    showDetails,
    learnLetters,
    learnIndex,
  ]);

  useEffect(() => {
    if (!session && !learnLetters) return;
    const card = cardRef.current;
    if (!card) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(card);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [fit, session, learnLetters]);

  useEffect(() => {
    if (session || learnLetters) {
      const body = document.body;
      const prevOverflow = body.style.overflow;
      const prevOverscroll = body.style.overscrollBehavior;
      body.style.overflow = "hidden";
      body.style.overscrollBehavior = "none";
      return () => {
        body.style.overflow = prevOverflow;
        body.style.overscrollBehavior = prevOverscroll;
      };
    }
  }, [session, learnLetters]);

  const letters = useMemo(
    () => ALL_LETTERS.filter((l) => selected.includes(l.groupId)),
    [selected],
  );

  const toggleGroup = (id) =>
    setSelected((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id],
    );

  const toggleAll = () =>
    setSelected(
      selected.length === ALPHABET_GROUPS.length
        ? []
        : ALPHABET_GROUPS.map((g) => g.id),
    );

  const beginQuiz = (questions) => {
    setSession(questions);
    setIndex(0);
    setPicked(null);
    setSkipped(false);
    setCorrect(0);
    setWrong([]);
    setDone(false);
    setBestStreak(0);
    setElapsedMs(0);
    setShowDetails(false);
    setShowToast(true);
    streakRef.current = 0;
    startTimeRef.current = Date.now();

    setLearnLetters(null);
    setPendingSession(null);
    setLearnIndex(0);
  };

  const start = () => {
    const questions = buildQuestions(letters, mode, count);
    const toLearn = lettersToLearn(questions);

    if (toLearn.length > 0) {
      setPendingSession(questions);
      setLearnLetters(toLearn);
      setLearnIndex(0);
    } else {
      beginQuiz(questions);
    }
  };

  const nextLearn = () => {
    if (learnIndex + 1 >= learnLetters.length) {
      beginQuiz(pendingSession);
    } else {
      setLearnIndex((i) => i + 1);
    }
  };

  const skipLearn = () => beginQuiz(pendingSession);

  const closeLearn = () => {
    setLearnLetters(null);
    setPendingSession(null);
    setLearnIndex(0);
  };

  const reset = () => {
    setSession(null);
    setDone(false);
    setLearnLetters(null);
    setPendingSession(null);
    setLearnIndex(0);
  };

  const answer = (opt) => {
    if (picked || skipped) return;
    setPicked(opt);
    const q = session[index];
    const ok = opt === q.answer;
    if (ok) {
      setCorrect((c) => c + 1);
      streakRef.current += 1;
      setBestStreak((b) => Math.max(b, streakRef.current));
    } else {
      streakRef.current = 0;
      setWrong((w) => (w.find((x) => x.char === q.char) ? w : [...w, q]));
    }
  };

  const dontKnow = () => {
    if (picked || skipped) return;
    const q = session[index];
    setSkipped(true);
    streakRef.current = 0;
    setWrong((w) => (w.find((x) => x.char === q.char) ? w : [...w, q]));
  };

  const next = () => {
    if (index + 1 >= session.length) {
      setElapsedMs(Date.now() - (startTimeRef.current || Date.now()));
      setDone(true);
    } else {
      setIndex((i) => i + 1);
      setPicked(null);
      setSkipped(false);
    }
  };

  const goPricing = () => navigate("/pricing");
  const redo = () => window.location.reload();
  const goSignup = () => navigate("/auth?mode=signup");

  /* ------------------------------------------- Learn + Quiz + End */
  if (session || learnLetters) {
    const inLearn = !!learnLetters;
    const q = !inLearn && session ? session[index] : null;
    const answered = !inLearn && (picked !== null || skipped);
    const progress = inLearn
      ? ((learnIndex + 1) / learnLetters.length) * 100
      : done
        ? 100
        : ((index + (answered ? 1 : 0)) / session.length) * 100;
    const learnLetter = inLearn ? learnLetters[learnIndex] : null;

    return (
      <div className="hf-alpha">
        <style>{STYLES}</style>
        <PublicNav />

        <div className="quiz-container">
          <div className="quiz-card" ref={cardRef}>
            <div className="q-inner" ref={innerRef}>
              {inLearn ? (
                <>
                  <div className="q-head">
                    <div className="q-head-row">
                      <h1 className="q-title">Learn the letters</h1>
                      <button
                        onClick={closeLearn}
                        aria-label="Close"
                        className="q-close"
                      >
                        <X className="q-ico-sm" />
                      </button>
                    </div>
                    <div className="q-progress-row">
                      <span>
                        Letter {learnIndex + 1} of {learnLetters.length}
                      </span>
                    </div>
                    <div className="q-bar">
                      <div
                        className="q-bar-fill"
                        style={{
                          width: `${progress}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="q-learn-body">
                    <div className="arabic q-char" dir="rtl">
                      {learnLetter.char}
                    </div>
                    <p className="q-learn-name">{learnLetter.name}</p>
                    <p className="q-learn-sound">{learnLetter.sound}</p>
                    <button
                      onClick={() => speak(learnLetter.word)}
                      className="q-speak"
                      aria-label="Hear it"
                    >
                      <Volume2 className="q-ico-md" />
                    </button>
                  </div>

                  <div className="q-learn-foot">
                    <button onClick={nextLearn} className="q-cta">
                      {learnIndex + 1 >= learnLetters.length
                        ? "Start the quiz"
                        : "Next letter"}
                    </button>
                    <button onClick={skipLearn} className="q-skip">
                      Skip to the quiz
                    </button>
                  </div>
                </>
              ) : !done ? (
                <>
                  <div className="q-head">
                    <div className="q-head-row">
                      <h1 className="q-title">Alphabet test</h1>
                      <button
                        onClick={reset}
                        aria-label="Close"
                        className="q-close"
                      >
                        <X className="q-ico-sm" />
                      </button>
                    </div>
                    <div className="q-progress-row">
                      <span>
                        Question {index + 1} of {session.length}
                      </span>
                      <span className="ok">{correct} right</span>
                    </div>
                    <div className="q-bar">
                      <div
                        className="q-bar-fill"
                        style={{
                          width: `${progress}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="q-body">
                    <div className="q-cols">
                      <div className="q-col-subject">
                        <div className="arabic q-char" dir="rtl">
                          {q.char}
                        </div>
                        <p className="q-prompt">{q.prompt}</p>
                        <button
                          onClick={() => speak(q.letter.word)}
                          className="q-speak"
                          aria-label="Hear it"
                        >
                          <Volume2 className="q-ico-md" />
                        </button>
                      </div>

                      <div className="q-col-options">
                        {q.options.map((opt, idx) => {
                          const isAns = opt === q.answer;
                          const show = answered;
                          const isPicked = !skipped && picked === opt;

                          let state = "";
                          if (show && isAns) state = " is-right";
                          else if (show && isPicked) state = " is-wrong";
                          else if (show) state = " is-muted";

                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => answer(opt)}
                              aria-disabled={show}
                              className={`q-option${state}`}
                            >
                              <span>{opt}</span>
                              {show && isAns && <Check className="q-ico-md" />}
                              {show && isPicked && !isAns && (
                                <X className="q-ico-md" />
                              )}
                            </button>
                          );
                        })}

                        {!answered && (
                          <button
                            type="button"
                            onClick={dontKnow}
                            className="q-hint"
                          >
                            I don&apos;t know
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="q-foot" aria-hidden={!answered}>
                    <button
                      onClick={next}
                      className="q-cta"
                      style={{
                        visibility: answered ? "visible" : "hidden",
                        pointerEvents: answered ? "auto" : "none",
                      }}
                      tabIndex={answered ? 0 : -1}
                    >
                      {index + 1 >= session.length ? "See my result" : "Next"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="q-end">
                  <h2 className="q-end-title">
                    {correct === session.length
                      ? "A clean sweep"
                      : "Test finished"}
                  </h2>

                  <div className="q-stats">
                    <div className="q-stat accent">
                      <div className="q-stat-value">
                        {session.length
                          ? Math.round((correct / session.length) * 100)
                          : 0}
                        %
                      </div>
                      <div className="q-stat-label">score</div>
                    </div>
                    <div className="q-stat">
                      <div className="q-stat-value">
                        {correct}/{session.length}
                      </div>
                      <div className="q-stat-label">right</div>
                    </div>
                    <div className="q-stat">
                      <div className="q-stat-value">{bestStreak}</div>
                      <div className="q-stat-label">best streak</div>
                    </div>
                    <div className="q-stat">
                      <div className="q-stat-value">
                        {formatDuration(elapsedMs)}
                      </div>
                      <div className="q-stat-label">time</div>
                    </div>
                  </div>

                  {wrong.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowDetails((s) => !s)}
                      className="q-details"
                    >
                      <ChevronDown
                        className={`q-chevron${showDetails ? " open" : ""}`}
                      />
                      <span>Letters to review</span>
                      <span className="q-badge">{wrong.length}</span>
                    </button>
                  )}

                  {showDetails && wrong.length > 0 && (
                    <div className="q-wrong-chars" dir="rtl">
                      {wrong.map((w, i) => (
                        <span key={i}>{w.char}</span>
                      ))}
                    </div>
                  )}

                  <div className="q-actions">
                    <button onClick={goPricing} className="q-cta">
                      Keep going with Harf
                    </button>
                    <button onClick={redo} className="q-redo">
                      <RotateCcw className="q-ico-sm" />
                      Take it again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {done && showToast && (
          <div
            className="signup-note"
            role="button"
            tabIndex={0}
            onClick={goSignup}
            onKeyDown={(e) => e.key === "Enter" && goSignup()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowToast(false);
              }}
              aria-label="Close"
              className="signup-close"
            >
              <X size={15} />
            </button>
            <span className="ar-mark" aria-hidden="true">
              حرف
            </span>
            <div>
              <h3>Keep this result</h3>
              <p>
                A free account saves your score and puts the letters you missed
                into review.
              </p>
              <span className="go">Create my account</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ------------------------------------------------------- Settings */
  return (
    <div className="hf-alpha">
      <style>{STYLES}</style>
      <PublicNav />

      <main className="set-page">
        <h1>Test your alphabet</h1>
        <p className="set-sub">
          Free, no account. Each letter is shown before it is tested.
        </p>
        <div className="set-rule" />

        <div className="set-row">
          <span className="set-label">Ask me for</span>
          <div className="seg">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={mode === m.id ? "on" : ""}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="set-row">
          <span className="set-label">Questions</span>
          <div className="step">
            <button
              onClick={() => setCount((c) => Math.max(5, c - 5))}
              aria-label="Fewer questions"
            >
              <Minus size={14} />
            </button>
            <span className="step-val">{count}</span>
            <button
              onClick={() => setCount((c) => Math.min(60, c + 5))}
              aria-label="More questions"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="grp-head">
          <span>Letter groups ({selected.length})</span>
          <button onClick={toggleAll}>
            {selected.length === ALPHABET_GROUPS.length
              ? "Clear all"
              : "Select all"}
          </button>
        </div>

        <div className="grp-grid">
          {ALPHABET_GROUPS.map((g) => {
            const on = selected.includes(g.id);
            return (
              <button
                key={g.id}
                onClick={() => toggleGroup(g.id)}
                aria-pressed={on}
                className={`grp${on ? " on" : ""}`}
              >
                <span className="grp-top">
                  <span className="grp-name">{g.label}</span>
                  {on && <Check size={13} />}
                </span>
                <span
                  className="arabic grp-letters"
                  dir="rtl"
                  style={{ display: "block" }}
                >
                  {g.letters.map((l) => l.char).join(" ")}
                </span>
              </button>
            );
          })}
        </div>
      </main>

      <div className="action-bar">
        <div className="inner">
          <button
            onClick={start}
            disabled={letters.length === 0}
            className="start-btn"
          >
            {letters.length === 0
              ? "Pick at least one group"
              : `Start · ${count} questions`}
          </button>
        </div>
      </div>
    </div>
  );
}
